export const ENV = 'develop'; // local

export type DatadogConfig = {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  defaultPrivacyLevel: string;
  forwardErrorsToLogs: boolean;
};

export type ConfigValue = {
  appName: string;
  appVersion: string;
  assetsDir: string;
  appMode: 'development' | 'production' | 'staging' | 'local';
  auth: {
    method: 'jwt'  | 'auth0';
    skip: boolean;
    redirectPath: string;
  };
  auth0: { clientId: string; domain: string; callbackUrl: string };
  develop: {
    baseUrl: string;
    appDomainUrl: string;
    candidateJobPortalUrl: string;
    enableInterviewLink: boolean;
  };
  datadog: DatadogConfig;
  productFruits: {
    workspaceCode: string;
    enabled: boolean;
  };
  billing: {
    pollIntervalMs: number;
    pollMaxAttemptsSuccess: number;
    pollMaxAttemptsPortal: number;
    pollInitialDelayMs: number;
  };
  googleTagManager: {
    id: string;
  };
};

export type RawConfig = {
  app?: {
    name?: string;
    version?: string;
    assetsDir?: string;
    appMode?: 'development' | 'production' | 'staging' | 'local';
  };
  auth?: {
    method?: string;
    skip?: boolean;
    redirectPath?: string;
  };
  auth0?: {
    clientId?: string;
    domain?: string;
    callbackUrl?: string;
  };
  environment?: {
    baseUrl?: string;
    appDomainUrl?: string;
    candidateJobPortalUrl?: string;
    enableInterviewLink?: boolean;
  };
  datadog?: {
    applicationId?: string;
    clientToken?: string;
    site?: string;
    service?: string;
    env?: string;
    sessionSampleRate?: number;
    sessionReplaySampleRate?: number;
    trackUserInteractions?: boolean;
    trackResources?: boolean;
    trackLongTasks?: boolean;
    defaultPrivacyLevel?: string;
    forwardErrorsToLogs?: boolean;
  };
  productFruits?: {
    workspaceCode?: string;
    enabled?: boolean;
  };
  googleTagManager?: {
    id?: string;
  };
};

let config: ConfigValue;

export async function loadConfig(): Promise<void> {
  
  const response = await fetch('/config.json');
  if (!response.ok) {
    throw new Error(`Failed to load config.json: ${response.statusText}`);
  }
  const rawConfig: RawConfig = await response.json();
  config = transformConfig(rawConfig);
}

export function getConfig() {
  if (!config) {
    throw new Error('Config has not been loaded yet.');
  }
  return config;
}

function transformConfig(rawConfig: RawConfig): ConfigValue {
  return {
    appName: rawConfig.app?.name ?? 'Default App Name',
    appVersion: rawConfig.app?.version ?? '0.0.1',
    assetsDir: rawConfig.app?.assetsDir ?? '',
    appMode: rawConfig.app?.appMode ?? 'development',
    auth: {
      method: rawConfig.auth?.method === 'auth0' ? 'auth0' : 'jwt',
      skip: rawConfig.auth?.skip ?? false,
      redirectPath: rawConfig.auth?.redirectPath ?? '/dashboard',
    },
    auth0: {
      clientId: rawConfig.auth0?.clientId ?? '',
      domain: rawConfig.auth0?.domain ?? '',
      callbackUrl: rawConfig.auth0?.callbackUrl ?? '',
    },
    develop: {
      baseUrl: rawConfig.environment?.baseUrl ?? '',
      appDomainUrl: rawConfig.environment?.appDomainUrl ?? '',
      candidateJobPortalUrl: rawConfig.environment?.candidateJobPortalUrl ?? '',
      enableInterviewLink: rawConfig.environment?.enableInterviewLink ?? false,
    },
    datadog: {
      applicationId: rawConfig.datadog?.applicationId ?? '',
      clientToken: rawConfig.datadog?.clientToken ?? '',
      site: rawConfig.datadog?.site ?? 'datadoghq.com',
      service: rawConfig.datadog?.service ?? 'hello-recruiter',
      env: rawConfig.datadog?.env ?? 'development',
      sessionSampleRate: rawConfig.datadog?.sessionSampleRate ?? 100,
      sessionReplaySampleRate: rawConfig.datadog?.sessionReplaySampleRate ?? 20,
      trackUserInteractions: rawConfig.datadog?.trackUserInteractions ?? true,
      trackResources: rawConfig.datadog?.trackResources ?? true,
      trackLongTasks: rawConfig.datadog?.trackLongTasks ?? true,
      defaultPrivacyLevel: rawConfig.datadog?.defaultPrivacyLevel ?? 'mask-user-input',
      forwardErrorsToLogs: rawConfig.datadog?.forwardErrorsToLogs ?? true,
    },
    productFruits: {
      workspaceCode: rawConfig.productFruits?.workspaceCode ?? '',
      enabled: rawConfig.productFruits?.enabled ?? false,
    },
    billing: {
      pollIntervalMs: 1000,
      pollMaxAttemptsSuccess: 15,
      pollMaxAttemptsPortal: 10,
      pollInitialDelayMs: 500,
    },
    googleTagManager: {
      id: rawConfig.googleTagManager?.id?.trim() ?? '',
    },
  };
}